import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import internalShipmentService, {
  InternalShipmentFilters,
  InternalShipmentRateTier,
} from '../../services/internalShipmentService';

export const useAdminInternalShipments = (filters: InternalShipmentFilters = {}) => {
  const shipmentsQuery = useQuery({
    queryKey: ['admin', 'internal-shipments', filters],
    queryFn: () => internalShipmentService.getAdminInternalShipments(filters),
  });

  const statsQuery = useQuery({
    queryKey: ['admin', 'internal-shipments', 'stats', filters.user_id, filters.search],
    queryFn: () => internalShipmentService.getAdminInternalShipmentStats(filters),
  });

  return {
    shipments: shipmentsQuery.data?.data || [],
    pagination: shipmentsQuery.data?.meta,
    stats: statsQuery.data?.data,
    isLoading: shipmentsQuery.isLoading || statsQuery.isLoading,
    shipmentsQuery,
    statsQuery,
  };
};

export const useAdminInternalShipment = (id?: string | number) => {
  const query = useQuery({
    queryKey: ['admin', 'internal-shipment', id],
    queryFn: () => internalShipmentService.getAdminInternalShipment(id!),
    enabled: !!id,
  });

  return {
    ...query,
    shipment: query.data?.data,
  };
};

export const useAdminInternalShipmentMutations = () => {
  const queryClient = useQueryClient();

  const updateStatus = useMutation({
    mutationFn: ({
      id,
      status,
      adminNotes,
    }: {
      id: string | number;
      status: 'approved' | 'rejected' | 'in_transit' | 'delivered';
      adminNotes?: string;
    }) => internalShipmentService.updateAdminInternalShipmentStatus(id, { status, adminNotes }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipments'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment', response.data.id] });
      queryClient.invalidateQueries({ queryKey: ['internal-shipment', response.data.id] });
    },
  });

  const updateTracking = useMutation({
    mutationFn: ({
      id,
      carrier,
      trackingNumber,
      adminNotes,
    }: {
      id: string | number;
      carrier: string;
      trackingNumber: string;
      adminNotes?: string;
    }) => internalShipmentService.updateAdminInternalShipmentTracking(id, { carrier, trackingNumber, adminNotes }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipments'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment', response.data.id] });
      queryClient.invalidateQueries({ queryKey: ['internal-shipment', response.data.id] });
    },
  });

  const saveRateCard = useMutation({
    mutationFn: ({
      stateId,
      isActive,
      notes,
      tiers,
    }: {
      stateId: number;
      isActive: boolean;
      notes?: string;
      tiers: InternalShipmentRateTier[];
    }) => internalShipmentService.upsertInternalShipmentRateCard(stateId, { isActive, notes, tiers }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment-rates'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment-rate', variables.stateId] });
    },
  });

  const importRateCards = useMutation({
    mutationFn: (file: File) => internalShipmentService.importInternalShipmentRates(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment-rates'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment-rate'] });
    },
  });

  const previewRateClone = useMutation({
    mutationFn: ({
      sourceStateId,
      targetStateIds,
    }: {
      sourceStateId: number;
      targetStateIds: number[];
    }) => internalShipmentService.previewInternalShipmentRateClone({ sourceStateId, targetStateIds }),
  });

  const cloneRateCards = useMutation({
    mutationFn: ({
      sourceStateId,
      targetStateIds,
    }: {
      sourceStateId: number;
      targetStateIds: number[];
    }) => internalShipmentService.cloneInternalShipmentRates({ sourceStateId, targetStateIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment-rates'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'internal-shipment-rate'] });
    },
  });

  return {
    updateStatus,
    updateTracking,
    saveRateCard,
    importRateCards,
    previewRateClone,
    cloneRateCards,
  };
};

export const useAdminInternalShipmentRateCard = (stateId?: number) => {
  const detailQuery = useQuery({
    queryKey: ['admin', 'internal-shipment-rate', stateId],
    queryFn: () => internalShipmentService.getInternalShipmentRateCard(stateId!),
    enabled: !!stateId,
  });

  const listQuery = useQuery({
    queryKey: ['admin', 'internal-shipment-rates'],
    queryFn: () => internalShipmentService.getInternalShipmentRateCards({ per_page: 500 }),
  });

  return {
    rateCardDetail: detailQuery.data?.data,
    configuredRateCards: listQuery.data?.data || [],
    isLoading: listQuery.isLoading || (!!stateId && detailQuery.isLoading),
    detailQuery,
    listQuery,
  };
};
